import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  LinearProgress, Alert, IconButton, Tooltip, Divider, Tabs, Tab,
  Switch, FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import SpeakerIcon from '@mui/icons-material/Speaker';
import LightModeIcon from '@mui/icons-material/LightMode';
import VideocamIcon from '@mui/icons-material/Videocam';
import {
  MY_CEREMONIES_QUERY,
  ENTERTAINMENT_ACTS_QUERY,
  MY_EVENT_PROGRAMS_QUERY,
  CREATE_EVENT_PROGRAM_MUTATION,
  UPDATE_EVENT_PROGRAM_MUTATION,
  DELETE_EVENT_PROGRAM_MUTATION,
  MY_STAGE_SETUPS_QUERY,
  CREATE_STAGE_SETUP_MUTATION,
  UPDATE_STAGE_SETUP_MUTATION,
  DELETE_STAGE_SETUP_MUTATION,
  VENDORS_QUERY,
} from '../graphql/queries';
import { useLov } from '../hooks/useLov';
import ContractedOfferingsPanel from '../components/ContractedOfferingsPanel';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const STATUS_COLOR = { planned: 'default', confirmed: 'success', completed: 'primary', cancelled: 'error' };
const AV_STATUS_COLOR = { planned: 'default', confirmed: 'info', setup: 'warning', active: 'success', dismantled: 'default' };
const EMPTY_FORM    = { programName: '', actId: '', customActName: '', startTime: '', durationMinutes: 60, notes: '', sortOrder: 0 };
const EMPTY_AV      = { ceremonyId: '', setupName: '', stageType: 'indoor', stageDimensions: '', backdropTheme: '', lightingType: '', soundSystem: '', screensCount: 0, projectorsCount: 0, microphonesCount: 0, liveStreaming: false, vendorId: '', setupCost: '', notes: '' };

export default function EntertainmentPage() {
  const [tab, setTab]                         = useState(0);
  const [selectedCeremonyId, setSelected]     = useState('');
  const [dialog, setDialog]                   = useState(false);
  const [editProg, setEdit]                   = useState(null);
  const [form, setForm]                       = useState(EMPTY_FORM);
  const [error, setError]                     = useState('');

  // Stage A/V state
  const [avDialog, setAvDialog]   = useState(false);
  const [editAv, setEditAv]       = useState(null);
  const [avForm, setAvForm]       = useState(EMPTY_AV);
  const [avCeremony, setAvCeremony] = useState('');

  const { data: ceremonyData }     = useQuery(MY_CEREMONIES_QUERY,       { fetchPolicy: 'cache-and-network' });
  const { data: actsData }         = useQuery(ENTERTAINMENT_ACTS_QUERY,  { fetchPolicy: 'cache-and-network' });
  const { data: vendorsData }      = useQuery(VENDORS_QUERY,             { fetchPolicy: 'cache-and-network' });
  const { data, loading, refetch } = useQuery(MY_EVENT_PROGRAMS_QUERY, {
    variables: { ceremonyId: selectedCeremonyId },
    skip: !selectedCeremonyId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: avData, loading: avLoading, refetch: refetchAv } = useQuery(MY_STAGE_SETUPS_QUERY, {
    variables: avCeremony ? { ceremonyId: avCeremony } : {},
    fetchPolicy: 'cache-and-network',
  });

  const ceremonies = ceremonyData?.myCeremonies?.filter(c => c.status !== 'cancelled') || [];
  const acts       = actsData?.entertainmentActs || [];
  const programs   = data?.myEventPrograms || [];
  const avSetups   = avData?.myStageSetups || [];
  const vendors    = vendorsData?.vendors || [];

  const { options: stageTypeOpts } = useLov('stage_type');

  const totalMinutes = programs.reduce((sum, p) => sum + (p.durationMinutes || 0), 0);
  const hours = Math.floor(totalMinutes / 60);
  const mins  = totalMinutes % 60;

  const [createProg] = useMutation(CREATE_EVENT_PROGRAM_MUTATION, {
    onCompleted: () => { setDialog(false); setForm(EMPTY_FORM); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [updateProg] = useMutation(UPDATE_EVENT_PROGRAM_MUTATION, {
    onCompleted: () => { setDialog(false); setEdit(null); setForm(EMPTY_FORM); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [deleteProg] = useMutation(DELETE_EVENT_PROGRAM_MUTATION, { onCompleted: () => refetch() });
  const [createAv] = useMutation(CREATE_STAGE_SETUP_MUTATION, {
    onCompleted: () => { setAvDialog(false); setAvForm(EMPTY_AV); refetchAv(); setError(''); },
    onError: e => setError(e.message),
  });
  const [updateAv] = useMutation(UPDATE_STAGE_SETUP_MUTATION, {
    onCompleted: () => { setAvDialog(false); setEditAv(null); refetchAv(); setError(''); },
    onError: e => setError(e.message),
  });
  const [deleteAv] = useMutation(DELETE_STAGE_SETUP_MUTATION, { onCompleted: () => refetchAv() });
  const openCreate = () => { setEdit(null); setForm({ ...EMPTY_FORM, sortOrder: programs.length }); setDialog(true); };
  const openEdit   = (p) => {
    setEdit(p);
    setForm({ programName: p.programName, actId: p.actId || '', customActName: p.customActName || '', startTime: p.startTime || '', durationMinutes: p.durationMinutes, notes: p.notes || '', sortOrder: p.sortOrder });
    setDialog(true);
  };
  const openCreateAv = () => { setEditAv(null); setAvForm({ ...EMPTY_AV, ceremonyId: avCeremony }); setAvDialog(true); };
  const openEditAv   = (s) => { setEditAv(s); setAvForm({ ceremonyId: s.ceremonyId || '', setupName: s.setupName, stageType: s.stageType, stageDimensions: s.stageDimensions || '', backdropTheme: s.backdropTheme || '', lightingType: s.lightingType || '', soundSystem: s.soundSystem || '', screensCount: s.screensCount || 0, projectorsCount: s.projectorsCount || 0, microphonesCount: s.microphonesCount || 0, liveStreaming: !!s.liveStreaming, vendorId: s.vendorId || '', setupCost: s.setupCost || '', notes: s.notes || '' }); setAvDialog(true); };
  const handleSaveAv = () => {
    const vars = { ...avForm, ceremonyId: avForm.ceremonyId || null, vendorId: avForm.vendorId || null, setupCost: Number(avForm.setupCost) || null, screensCount: Number(avForm.screensCount) || 0, projectorsCount: Number(avForm.projectorsCount) || 0, microphonesCount: Number(avForm.microphonesCount) || 0 };
    if (editAv) updateAv({ variables: { id: editAv.id, ...vars } });
    else createAv({ variables: vars });
  };
  const handleSave = () => {
    const vars = { ...form, durationMinutes: Number(form.durationMinutes) || 60, sortOrder: Number(form.sortOrder) || 0, actId: form.actId || null, ceremonyId: selectedCeremonyId };
    if (editProg) updateProg({ variables: { id: editProg.id, ...vars } });
    else createProg({ variables: vars });
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Entertainment &amp; Programs</Typography>
          <Typography variant="body2" color="text.secondary">Schedule cultural programs, orchestras, and manage stage &amp; A/V setup.</Typography>
        </Box>
        {tab === 0 && <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={!selectedCeremonyId}>Add Program</Button>}
        {tab === 1 && <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateAv}>Add Stage / A/V Setup</Button>}
      </Stack>

      <ContractedOfferingsPanel categories={['entertainment', 'music_entertainment']} title="Contracted entertainers" />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Cultural Programs" />
        <Tab label="Stage & A/V Setup" />
      </Tabs>

      {/* ── TAB 0 : Cultural Programs ── */}
      {tab === 0 && (<>

      {/* Ceremony selector */}
      <Card sx={{ mb: 3, px: 2, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>SELECT CEREMONY</Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {ceremonies.map(c => (
            <Chip key={c.id} label={c.name} onClick={() => setSelected(c.id === selectedCeremonyId ? '' : c.id)} color={selectedCeremonyId === c.id ? 'primary' : 'default'} variant={selectedCeremonyId === c.id ? 'filled' : 'outlined'} icon={<MusicNoteIcon />} />
          ))}
        </Stack>
      </Card>

      {!selectedCeremonyId && <Alert severity="info">Select a ceremony above to manage its entertainment schedule.</Alert>}

      {selectedCeremonyId && (
        <>
          {/* Duration summary */}
          {programs.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Total program time: <strong>{hours > 0 ? `${hours}h ` : ''}{mins}m</strong> across {programs.length} program(s).
            </Alert>
          )}

          {loading && <LinearProgress sx={{ mb: 2 }} />}
          {programs.length === 0 && !loading && <Alert severity="info">No programs scheduled. Click <strong>Add Program</strong> to begin.</Alert>}

          <Grid container spacing={2}>
            <Paged items={programs}>{rows => rows.map(p => (
              <Grid item xs={12} sm={6} md={4} key={p.id}>
                <Card>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center" mb={0.5}>
                          <MusicNoteIcon color="primary" fontSize="small" />
                          <Typography variant="subtitle2" fontWeight={700}>{p.programName}</Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block">{p.act?.name || p.customActName || 'Custom act'}</Typography>
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
                          <Chip label={p.status} size="small" color={STATUS_COLOR[p.status] || 'default'} />
                          {p.startTime && <Chip label={p.startTime} size="small" variant="outlined" />}
                          <Chip label={`${p.durationMinutes}m`} size="small" variant="outlined" />
                        </Stack>
                      </Box>
                      <Stack direction="row">
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(p)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deleteProg({ variables: { id: p.id } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}</Paged>
          </Grid>
        </>
      )}

      {/* Available Acts catalog */}
      {acts.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>Available Entertainment Acts</Typography>
          <Grid container spacing={2}>
            <Paged items={acts}>{rows => rows.map(act => (
              <Grid item xs={12} sm={6} md={4} key={act.id}>
                <Card variant="outlined">
                  <CardContent sx={{ py: '12px !important' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <MusicNoteIcon color="primary" />
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>{act.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{act.city} • {act.actType.replace(/_/g,' ')}</Typography>
                        {act.rating && <Chip label={`Rating ${act.rating}`} size="small" color="warning" sx={{ ml: 0.5, height: 18, fontSize: '0.6rem' }} />}
                      </Box>
                    </Stack>
                    {act.flatRate && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Flat rate: ₹{Number(act.flatRate).toLocaleString('en-IN')}</Typography>}
                    {act.pricePerHour && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>₹{Number(act.pricePerHour).toLocaleString('en-IN')}/hr</Typography>}
                  </CardContent>
                </Card>
              </Grid>
            ))}</Paged>
          </Grid>
        </Box>
      )}

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editProg ? 'Edit Program' : 'Schedule Program'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Program Name" value={form.programName} onChange={e => setForm(f => ({ ...f, programName: e.target.value }))} fullWidth required autoFocus placeholder="e.g. Carnatic Recital, Nalangu Games" />
            <TextField select label="Entertainment Act (from catalogue)" value={form.actId} onChange={e => setForm(f => ({ ...f, actId: e.target.value }))} fullWidth>
              <MenuItem value="">— Custom / Not in catalogue —</MenuItem>
              {acts.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
            </TextField>
            {!form.actId && (
              <TextField label="Act Name (custom)" value={form.customActName} onChange={e => setForm(f => ({ ...f, customActName: e.target.value }))} fullWidth placeholder="e.g. Local Nadaswaram group" />
            )}
            <Stack direction="row" spacing={2}>
              <TextField label="Start Time" value={form.startTime} onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))} fullWidth placeholder="e.g. 09:00 AM" />
              <TextField label="Duration (minutes)" type="number" value={form.durationMinutes} onChange={e => setForm(f => ({ ...f, durationMinutes: e.target.value }))} fullWidth />
              <TextField label="Sort Order" type="number" value={form.sortOrder} onChange={e => setForm(f => ({ ...f, sortOrder: e.target.value }))} fullWidth />
            </Stack>
            <TextField label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.programName.trim()}>Save</Button>
        </DialogActions>
      </Dialog>
      </>)}

      {/* ── TAB 1 : Stage & A/V Setup ── */}
      {tab === 1 && (
        <Box>
          {/* Ceremony filter */}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
            <Chip label="All Ceremonies" onClick={() => setAvCeremony('')} color={!avCeremony ? 'primary' : 'default'} variant={!avCeremony ? 'filled' : 'outlined'} />
            {ceremonies.map(c => <Chip key={c.id} label={c.name} onClick={() => setAvCeremony(c.id === avCeremony ? '' : c.id)} color={avCeremony === c.id ? 'primary' : 'default'} variant={avCeremony === c.id ? 'filled' : 'outlined'} />)}
          </Stack>
          {avLoading && <LinearProgress sx={{ mb: 2 }} />}
          {avSetups.length === 0 && !avLoading && <Alert severity="info">No stage/AV setups yet. Click <strong>Add Stage / A/V Setup</strong> to plan your stage.</Alert>}
          <Grid container spacing={2}>
            <Paged items={avSetups}>{rows => rows.map(s => (
              <Grid item xs={12} sm={6} md={4} key={s.id}>
                <Card>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography fontWeight={700}>{s.setupName}</Typography>
                      <Chip label={s.status} size="small" color={AV_STATUS_COLOR[s.status] || 'default'} />
                    </Stack>
                    {s.ceremony && <Chip label={s.ceremony.name} size="small" sx={{ mb: 1 }} variant="outlined" />}
                    <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
                      <Chip label={s.stageType} size="small" />
                      {s.stageDimensions && <Chip label={s.stageDimensions} size="small" variant="outlined" />}
                    </Stack>
                    <Grid container spacing={1}>
                      {s.lightingType && <Grid item xs={12}><Stack direction="row" spacing={0.5}><LightModeIcon fontSize="small" color="warning" /><Typography variant="caption">{s.lightingType}</Typography></Stack></Grid>}
                      {s.soundSystem   && <Grid item xs={12}><Stack direction="row" spacing={0.5}><SpeakerIcon fontSize="small" color="info" /><Typography variant="caption">{s.soundSystem}</Typography></Stack></Grid>}
                      {(s.screensCount > 0 || s.projectorsCount > 0 || s.microphonesCount > 0) && (
                        <Grid item xs={12}>
                          <Stack direction="row" spacing={1}>
                            {s.screensCount > 0 && <Chip icon={<VideocamIcon />} label={`${s.screensCount} screen${s.screensCount > 1 ? 's' : ''}`} size="small" />}
                            {s.projectorsCount > 0 && <Chip label={`${s.projectorsCount} projector${s.projectorsCount > 1 ? 's' : ''}`} size="small" />}
                            {s.microphonesCount > 0 && <Chip label={`${s.microphonesCount} mic${s.microphonesCount > 1 ? 's' : ''}`} size="small" />}
                          </Stack>
                        </Grid>
                      )}
                      {s.liveStreaming && <Grid item xs={12}><Chip label="Live Streaming" size="small" color="error" /></Grid>}
                      {s.setupCost && <Grid item xs={12}><Typography variant="caption" color="text.secondary">Cost: ₹{Number(s.setupCost).toLocaleString('en-IN')}</Typography></Grid>}
                      {s.vendor && <Grid item xs={12}><Typography variant="caption" color="text.secondary">Vendor: {s.vendor.name}</Typography></Grid>}
                    </Grid>
                    <Stack direction="row" justifyContent="flex-end" spacing={0.5} sx={{ mt: 1 }}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditAv(s)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deleteAv({ variables: { id: s.id } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}</Paged>
          </Grid>
        </Box>
      )}

      {/* Stage A/V Dialog */}
      <Dialog open={avDialog} onClose={() => setAvDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editAv ? 'Edit Stage / A/V Setup' : 'New Stage / A/V Setup'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField label="Setup Name" value={avForm.setupName} onChange={e => setAvForm(f => ({ ...f, setupName: e.target.value }))} fullWidth required placeholder="e.g. Mandap Stage, Reception Stage" />
              <TextField select label="Stage Type" value={avForm.stageType} onChange={e => setAvForm(f => ({ ...f, stageType: e.target.value }))} fullWidth>
                {stageTypeOpts.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField select label="Ceremony" value={avForm.ceremonyId} onChange={e => setAvForm(f => ({ ...f, ceremonyId: e.target.value }))} fullWidth>
                <MenuItem value="">None</MenuItem>
                {ceremonies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
              <TextField label="Stage Dimensions" value={avForm.stageDimensions} onChange={e => setAvForm(f => ({ ...f, stageDimensions: e.target.value }))} fullWidth placeholder="e.g. 20ft x 16ft" />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Backdrop / Theme" value={avForm.backdropTheme} onChange={e => setAvForm(f => ({ ...f, backdropTheme: e.target.value }))} fullWidth placeholder="e.g. Floral arch, LED wall" />
              <TextField label="Lighting Type" value={avForm.lightingType} onChange={e => setAvForm(f => ({ ...f, lightingType: e.target.value }))} fullWidth placeholder="e.g. LED Wash, Spotlights" />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Sound System" value={avForm.soundSystem} onChange={e => setAvForm(f => ({ ...f, soundSystem: e.target.value }))} fullWidth placeholder="e.g. Line Array, 4000W" />
              <TextField label="Setup Cost (₹)" type="number" value={avForm.setupCost} onChange={e => setAvForm(f => ({ ...f, setupCost: e.target.value }))} fullWidth />
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={4}><TextField label="Screens" type="number" value={avForm.screensCount} onChange={e => setAvForm(f => ({ ...f, screensCount: e.target.value }))} fullWidth /></Grid>
              <Grid item xs={4}><TextField label="Projectors" type="number" value={avForm.projectorsCount} onChange={e => setAvForm(f => ({ ...f, projectorsCount: e.target.value }))} fullWidth /></Grid>
              <Grid item xs={4}><TextField label="Microphones" type="number" value={avForm.microphonesCount} onChange={e => setAvForm(f => ({ ...f, microphonesCount: e.target.value }))} fullWidth /></Grid>
            </Grid>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControlLabel control={<Switch checked={!!avForm.liveStreaming} onChange={e => setAvForm(f => ({ ...f, liveStreaming: e.target.checked }))} />} label="Live Streaming" />
              <TextField select label="A/V Vendor" value={avForm.vendorId} onChange={e => setAvForm(f => ({ ...f, vendorId: e.target.value }))} fullWidth>
                <MenuItem value="">None</MenuItem>
                {vendors.map(v => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
              </TextField>
            </Stack>
            <TextField label="Notes" value={avForm.notes} onChange={e => setAvForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAvDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAv}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
